import { useEffect, useMemo, useState } from "react"

import { Flex, Spin } from "antd"
import dayjs from "dayjs"
import { useDispatch, useSelector } from "react-redux"

import SelectComponent from "../../../../../Components/Select"
import Images from "../../../../../Images"
import { useDemoDataToggle } from "../../../../User/hooks/useDemoDataToggle"
import HealthActions from "../../../reducer"
import { useVampHistoricalDataMode } from "../../hooks/useHistoricalDataMode"
import useMonitoringProgramProgress from "../../hooks/useMonitoringProgramProgress"
import { useVampDataAvailable } from "../../hooks/useVampDataAvailable"
import InfoLine from "../InfoLine/InfoLine"
import SectionHeader from "../SectionHeader/SectionHeader"
import MonitoringProgramEdges from "./MonitoringProgramEdges/MonitoringProgramEdges"
import MonthBlock from "./MonthBlock/MonthBlock"
import MonitoringProgramStatus from "./ProgramStatus/ProgramStatus"
import ProgressBar from "./ProgressBar/ProgressBar"

import "./MonitoringProgram.less"

const statusMessageTextMap = {
  pending: null,
  compliant: "Your account is in good health and within all acceptable thresholds of ECM.",
  warning:
    "Your chargeback rate has exceeded the acceptable limit. If not addressed, you will enter the monitoring program.",
  under_monitoring:
    "You’re currently under monitoring. Reduce chargebacks to exit the program and avoid penalties.",
  no_status: null
}

const statusMessageVariantMap = {
  pending: "neutral",
  compliant: "success",
  warning: "warning",
  under_monitoring: "warning",
  no_status: "neutral"
}

const MonitoringProgram = ({ business_account_id }) => {
  const dispatch = useDispatch()
  const { isMobile } = useSelector((state) => state.nav)
  const { vampMonitoringProgramChart, vampMonitoringProgramChartFetching, descriptor } =
    useSelector((state) => state.health)
  const data = vampMonitoringProgramChart ?? {}
  const currentStatus = data?.current_status
  const { isShowDemoData } = useDemoDataToggle()
  const { isHistoricalDataMode } = useVampHistoricalDataMode()
  const isVampDataAvailable = useVampDataAvailable()

  const yearOptions = useMemo(() => {
    const options = vampMonitoringProgramChart?.available_years ?? [new Date().getFullYear()]
    const years = []
    for (const y of options) {
      years.push({ label: y, value: y })
    }
    return years
  }, [vampMonitoringProgramChart])
  const [year, setYear] = useState(yearOptions?.[yearOptions.length - 1]?.value ?? null)

  useEffect(() => {
    if (!vampMonitoringProgramChart?.available_years?.includes(year)) {
      setYear(yearOptions?.[yearOptions.length - 1]?.value ?? null)
    }
  }, [yearOptions])

  useEffect(() => {
    if (descriptor && isVampDataAvailable) {
      dispatch(
        HealthActions.getVampMonitoringProgramChartRequest({
          business_account: business_account_id,
          year,
          descriptor,
          ...(isHistoricalDataMode ? { historical_data: true } : {})
        })
      )
    }
  }, [
    business_account_id,
    year,
    isShowDemoData,
    descriptor,
    isHistoricalDataMode,
    isVampDataAvailable
  ])

  const {
    isShowProgressBar,
    timeLineSegments,
    startDate,
    endDate,
    edgesIntervalWidth,
    edgesStartOffset,
    isFinishTextHidden
  } = useMonitoringProgramProgress({ data })

  const startMonth = startDate ? dayjs(startDate).month() + 1 : null
  const endMonth = endDate ? dayjs(endDate).month() : null

  const timelineRows = useMemo(() => {
    const timeline = data?.timeline ?? []
    const rows = []

    for (let i = 0; i < timeline.length; i += 2) {
      rows.push(timeline.slice(i, i + 2))
    }

    return rows
  }, [data?.timeline])

  const mobileTimelineItems = useMemo(() => {
    if (!isMobile) return []

    const items = []
    let finishInserted = false

    timelineRows.forEach((row) => {
      const rowStartMonth = row?.[0]?.month
      const hasStartMonth = startMonth && row?.some((m) => m.month === startMonth)

      if (isShowProgressBar && hasStartMonth) {
        items.push({ type: "marker", markerType: "start" })
      }

      if (isShowProgressBar && !finishInserted && endMonth && rowStartMonth > endMonth) {
        items.push({ type: "marker", markerType: "finish" })
        finishInserted = true
      }

      items.push({ type: "row", row })
    })

    if (isShowProgressBar && !finishInserted && !isFinishTextHidden) {
      items.push({ type: "marker", markerType: "finish" })
    }

    return items
  }, [isMobile, timelineRows, isShowProgressBar, startMonth, endMonth, isFinishTextHidden])

  return (
    <Flex vertical>
      <Spin
        wrapperClassName="monitoring-program-chart-spin"
        spinning={vampMonitoringProgramChartFetching}
      >
        <Flex
          gap={10}
          align="flex-start"
          justify="space-between"
          className="monitoring-program-header"
        >
          <div>
            <SectionHeader
              title="Visa Acquirer Monitoring Program (VAMP)"
              image={Images.visa_3d}
              marginBottom={8}
            />
            <MonitoringProgramStatus status={currentStatus} styles={{ marginBottom: 16 }} />
          </div>
          <SelectComponent
            value={year}
            onChange={setYear}
            options={yearOptions}
            style={{ width: isMobile ? "100%" : 100 }}
            className="monitoring-program-year-select"
          />
        </Flex>

        {!isMobile && isShowProgressBar && (
          <>
            <Flex vertical gap={8}>
              <MonitoringProgramEdges
                intervalWidth={edgesIntervalWidth}
                startOffset={edgesStartOffset}
                isFinishTextHidden={isFinishTextHidden}
                startDate={startDate}
                endDate={endDate}
              />
              <Flex gap={4} style={{ width: "100%" }}>
                {timeLineSegments.map((segment, index) => (
                  <ProgressBar
                    key={index}
                    progress={segment.progress}
                    style={{ width: segment.width }}
                  />
                ))}
              </Flex>
            </Flex>
            <Flex style={{ marginTop: 12 }}>
              {data?.timeline?.map((month) => (
                <MonthBlock
                  key={month.month}
                  currentMonth={data?.current_month?.month}
                  data={month}
                  styles={{ flex: 1 }}
                />
              ))}
            </Flex>
            {statusMessageTextMap[currentStatus?.status] && (
              <InfoLine
                text={statusMessageTextMap[currentStatus?.status]}
                variant={statusMessageVariantMap[currentStatus?.status]}
                style={{ marginTop: "16px" }}
              />
            )}
          </>
        )}

        {isMobile ? (
          <div className="monitoring-program-mobile" style={{ marginTop: 16 }}>
            {mobileTimelineItems.map((item, index) => {
              if (item.type === "marker") {
                const isStart = item.markerType === "start"

                return (
                  <div
                    key={`marker-${item.markerType}-${index}`}
                    className="monitoring-program-mobile__marker"
                  >
                    {isStart
                      ? `${dayjs(startDate).format("D MMMM, YYYY")} - Monitoring program start`
                      : `${dayjs(endDate).format("D MMMM, YYYY")} - Monitoring program finish`}
                  </div>
                )
              }

              return (
                <Flex key={`row-${index}`} gap={12} className="monitoring-program-mobile__row">
                  {item.row.map((month) => (
                    <MonthBlock
                      key={month.month}
                      currentMonth={data?.current_month?.month}
                      data={month}
                      styles={{ flex: 1 }}
                      isMobile
                    />
                  ))}
                </Flex>
              )
            })}
          </div>
        ) : (
          <Flex style={{ marginTop: 12 }}>
            {data?.timeline?.map((month) => (
              <MonthBlock
                key={month.month}
                currentMonth={data?.current_month?.month}
                data={month}
                styles={{ flex: 1 }}
              />
            ))}
          </Flex>
        )}

        {isMobile && statusMessageTextMap[currentStatus?.status] && (
          <InfoLine
            text={statusMessageTextMap[currentStatus?.status]}
            variant={statusMessageVariantMap[currentStatus?.status]}
            style={{ marginTop: "16px" }}
          />
        )}
      </Spin>
    </Flex>
  )
}

export default MonitoringProgram
